import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import isEmpty from 'lodash/isEmpty';

interface TagsPickerProps {
  tags: Record<string, string> | any;
  label?: string;
  value?: string[];
  multiple?: boolean;
  col?: boolean;
  className?: string;
  onChange?: (result: any) => void;
  helperText?: string | React.ReactNode;
  error?: boolean;
  sx?: React.CSSProperties;
  checkIcon?: React.ReactNode;
  tagSx?: React.CSSProperties | Record<string, React.CSSProperties>;
  labelSx?: React.CSSProperties;
}

const TagsPicker = (props: TagsPickerProps) => {
  const {
    label,
    tags,
    value = [],
    multiple = false,
    col = false,
    className = '',
    onChange,
    error = false,
    helperText = '',
    sx = {},
    checkIcon = <CheckCircleIcon />,
    tagSx = {},
    labelSx = {},
  } = props;

  const isChecked = (checkedValue: string) => {
    return (
      Array.isArray(value) &&
      !isEmpty(value.filter((item: string) => item === checkedValue))
    );
  };

  const handleSelectChange = (checkedValue: string) => {
    const isChangeValue = value.includes(checkedValue);
    if (multiple && isChangeValue) {
      const newValue = value.filter((item) => item !== checkedValue);
      onChange && onChange(newValue);
      return;
    }
    if (multiple) {
      onChange && onChange([...value, checkedValue]);
    } else {
      !isChangeValue && onChange && onChange([checkedValue]);
    }
  };

  const newTags = Array.isArray(tags) ? tags : Object.keys(tags);

  return (
    <Box className={`flex flex-col w-auto ${className}`} sx={sx}>
      {label && (
        <FormLabel
          className="!mb-2 !text-base !font-semibold !text-black !items-center tablet:!text-sm tablet:!mb-3"
          sx={{ ...labelSx }}
        >
          {label}
        </FormLabel>
      )}
      <div
        className={`${
          col
            ? 'flex flex-col justify-center gap-4'
            : 'flex flex-row items-center flex-wrap gap-5'
        }`}
      >
        {newTags.length > 0
          ? newTags.map((key: string) => (
              <Button
                className={`!text-black !flex !items-center ${
                  col
                    ? '!text-sm !bg-blue-50 !rounded-lg !justify-start'
                    : '!text-sm !leading-5 !bg-slate-50 !justify-center !rounded-full tablet:!text-xs'
                } ${isChecked(key) ? 'checked' : ''}`}
                key={key}
                onClick={() => handleSelectChange(key)}
                startIcon={col && isChecked(key) && checkIcon}
                endIcon={!col && isChecked(key) && checkIcon}
                sx={{
                  border: '0px',
                  padding: col
                    ? '11.5px 16px !important'
                    : '8px 20px !important',
                  '&.checked': {
                    backgroundColor: col
                      ? '#0c4a6e !important'
                      : '#0050b3 !important',
                    color: '#ffffff !important',
                  },
                  ':hover': {
                    backgroundColor: '#dbeafe !important',
                  },
                  '&:hover.checked': {
                    backgroundColor: col
                      ? '#0c4a6e !important'
                      : '#0050b3 !important',
                  },
                  ':active': {
                    backgroundColor: col
                      ? '#0c4a6e !important'
                      : '#0050b3 !important',
                    color: '#ffffff !important',
                  },
                  '.MuiButton-endIcon': {
                    marginLeft: '5.67px !important',
                  },
                  ...tagSx,
                }}
              >
                {Array.isArray(tags) ? key : tags[key]}
              </Button>
            ))
          : null}
      </div>
      {error && helperText && (
        <p className="text-red-700 text-xs mt-2 inline-block">{helperText}</p>
      )}
    </Box>
  );
};

export default TagsPicker;
